/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { Alert, Button } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { IQuickOrderState } from '../quick-order';

export interface IQuickOrderFormProps {
    disableAddtoCart: boolean | undefined;
    productTextInput: React.RefObject<HTMLInputElement>;
    quantityTextInput: React.RefObject<HTMLInputElement>;
    maxChars: number;
    maxQuantity?: number;
    ariaLabelQuantity: string;
    ariaLabelProduct: string;
    ariaLabelAddtoCart: string;
    quickOrderState: IQuickOrderState;
    handleInputChange(query: string): void;
    handleQuantityChange(query: string): void;
    handleAddToCartClick(): Promise<void>;
}

export interface IQuickOrderFormViewProps {
    product: React.ReactNode;
    quantity: React.ReactNode;
    addToCartBtn: React.ReactNode;
    userMessage?: React.ReactNode;
}

/**
 * On Input Change functionality.
 * @param handleInputChange -Input change function.
 * @returns Input change value.
 */
const onInputChangeFunction = (handleInputChange: (query: string) => void) => (event: React.ChangeEvent<HTMLInputElement>) => {
    handleInputChange(event.target.value || '');
};

/**
 * On Quantity Change functionality.
 * @param handleQuantityChange -Quantity change function.
 * @returns Quantity change value.
 */
const onQuantityChangeFunction = (handleQuantityChange: (query: string) => void) => (event: React.ChangeEvent<HTMLInputElement>) => {
    handleQuantityChange(event.target.value || '');
};

export const QuickOrderFormComponent = (props: IQuickOrderFormProps): IQuickOrderFormViewProps => {
    const {
        productTextInput,
        quantityTextInput,
        quickOrderState,
        maxChars,
        maxQuantity,
        disableAddtoCart,
        ariaLabelQuantity,
        ariaLabelProduct,
        ariaLabelAddtoCart,
        handleQuantityChange,
        handleInputChange,
        handleAddToCartClick
    } = props;

    const onInputChange = onInputChangeFunction(handleInputChange);

    const onQuantityInputChange = onQuantityChangeFunction(handleQuantityChange);

    const productNode = (
        <div className='ms-quick-order__content-form-main-product'>
            <h2 className='ms-quick-order__content-form-main-product-lable' aria-label={ariaLabelProduct}>
                {ariaLabelProduct}
            </h2>
            <input
                className='ms-quick-order__content-form-main-product-input'
                type='text'
                aria-label={ariaLabelProduct}
                ref={productTextInput}
                onChange={onInputChange}
            />
        </div>
    );

    const quantityNode = (
        <div className='ms-quick-order__content-form-main-quantity'>
            <h2 className='ms-quick-order__content-form-main-quantity-lable' aria-label={ariaLabelQuantity}>
                {ariaLabelQuantity}
            </h2>
            <input
                className='ms-quick-order__content-form-main-quantity-input'
                pattern='[0-9]*'
                ref={quantityTextInput}
                onChange={onQuantityInputChange}
                maxLength={maxChars}
                type='number'
                aria-live='polite'
                aria-label={ariaLabelQuantity}
                aria-valuemin={0}
                aria-valuemax={maxQuantity}
                aria-valuenow={quickOrderState.quantity}
            />
        </div>
    );

    const addtoCartNode = (
        <div className='ms-quick-order__content-form-main-addtocart'>
            <Button
                className='ms-quick-order__content-form-main-addtocart-button' disabled={disableAddtoCart} aria-label={ariaLabelAddtoCart}
                onClick={handleAddToCartClick}>
                {ariaLabelAddtoCart}
            </Button>
        </div>
    );

    const userMessageNode = quickOrderState && quickOrderState.stateMessage ? (
        <Alert color={quickOrderState.stateMessage.statusCssString} isOpen={quickOrderState.stateMessage.isOpen}>
            <span className={quickOrderState.stateMessage.messageGlyph} />
            <span>
                {quickOrderState.stateMessage.messageText}
            </span>
        </Alert>
    ) : undefined;

    return {
        product: productNode,
        addToCartBtn: addtoCartNode,
        quantity: quantityNode,
        userMessage: userMessageNode
    };
};
